import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Trash2, Plus, ChevronDown, ChevronRight, Library } from "lucide-react";
import * as api from "../../lib/api";

/** Edit a student's IEP Meeting Due Date + Goal Report Due Date. */
export function DueDatesDialog({ open, onOpenChange, student, onSaved }) {
  const [iep, setIep] = useState(student?.iep_meeting_due_date || "");
  const [report, setReport] = useState(student?.goal_report_due_date || "");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setIep(student?.iep_meeting_due_date || "");
    setReport(student?.goal_report_due_date || "");
  }, [student]);

  const save = async () => {
    if (!student) return;
    setSaving(true);
    try {
      await api.setGoalDueDates(student.id, { iep_meeting_due_date: iep, goal_report_due_date: report });
      toast.success("Due dates saved");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error("Failed to save due dates"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="due-dates-dialog">
        <DialogHeader>
          <DialogTitle>Special Educator Due Dates</DialogTitle>
          <DialogDescription>
            {student?.name}{student?.special_educator_name ? ` · Special Educator: ${student.special_educator_name}` : " · No special educator assigned"}.
            A reminder (in-app + email) goes to the assigned special educator two weeks before each date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>IEP Meeting Due Date</Label>
            <Input type="date" value={iep} onChange={(e) => setIep(e.target.value)} data-testid="iep-due-date-input" />
          </div>
          <div>
            <Label>Goal Report Due Date</Label>
            <Input type="date" value={report} onChange={(e) => setReport(e.target.value)} data-testid="goal-report-due-date-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-due-dates">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Manage the school's date-based reporting periods. */
export function ReportingPeriodsDialog({ open, onOpenChange, periods = [], onChanged }) {
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!form.name.trim() || !form.start_date || !form.end_date) { toast.error("Name, start and end dates required"); return; }
    setSaving(true);
    try {
      await api.createGoalReportingPeriod(form);
      setForm({ name: "", start_date: "", end_date: "" });
      toast.success("Reporting period added");
      onChanged && onChanged();
    } catch (e) { toast.error("Failed to add period"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.deleteGoalReportingPeriod(id); toast.success("Deleted"); onChanged && onChanged(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reporting-periods-dialog">
        <DialogHeader>
          <DialogTitle>Reporting Periods</DialogTitle>
          <DialogDescription>Define date-based reporting windows (not tied to the calendar). Goals and reports can be tagged to a period.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-52 overflow-y-auto">
          {periods.length === 0 && <p className="text-sm text-slate-400 italic">No reporting periods yet.</p>}
          {periods.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid={`period-row-${p.id}`}>
              <div>
                <div className="text-sm font-medium">{p.name}</div>
                <div className="text-xs text-slate-500">{p.start_date} → {p.end_date}</div>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(p.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
        <div className="rounded-md border border-slate-200 p-3 space-y-2">
          <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Period name (e.g. Q1 2026-2027)" data-testid="period-name-input" />
          <div className="flex gap-2">
            <div className="flex-1">
              <Label className="text-xs">Start</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} data-testid="period-start-input" />
            </div>
            <div className="flex-1">
              <Label className="text-xs">End</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} data-testid="period-end-input" />
            </div>
          </div>
          <Button size="sm" onClick={add} disabled={saving} data-testid="add-period-btn"><Plus className="w-4 h-4 mr-1" />Add Period</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Browse & manage all saved goal templates, grouped by goal type. */
export function TemplatesLibraryDialog({ open, onOpenChange, templates = [], onChanged }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const del = async (id) => {
    try { await api.deleteGoalTemplate(id); toast.success("Template deleted"); onChanged && onChanged(); }
    catch (e) { toast.error("Failed to delete template"); }
  };

  // Group by goal type (categories); untyped templates fall under "Uncategorized".
  const groups = {};
  for (const t of templates) {
    const key = (t.goal_type || "").trim() || "Uncategorized";
    (groups[key] = groups[key] || []).push(t);
  }
  const categories = Object.keys(groups).sort((a, b) => a.localeCompare(b));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="templates-library-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Library className="w-5 h-5" />Goal Template Library</DialogTitle>
          <DialogDescription>Browse and manage every saved goal template, grouped by goal type. Create templates via a goal card's "Save as template".</DialogDescription>
        </DialogHeader>
        {templates.length === 0 ? (
          <p className="text-sm text-slate-400 italic py-6 text-center">No templates yet. Open a goal and choose "Save as template" to create one.</p>
        ) : (
          <div className="space-y-4">
            {categories.map((cat) => (
              <div key={cat} data-testid={`template-category-${cat}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#00305E]">{cat}</span>
                  <span className="text-xs text-slate-400">({groups[cat].length})</span>
                </div>
                <div className="space-y-2">
                  {groups[cat].map((t) => {
                    const isOpen = expanded.has(t.id);
                    const objs = t.objectives || [];
                    return (
                      <div key={t.id} className="rounded-md border border-slate-200" data-testid={`template-row-${t.id}`}>
                        <div className="flex items-center justify-between px-3 py-2">
                          <button className="flex items-center gap-1.5 min-w-0 text-left" onClick={() => toggle(t.id)} data-testid={`template-toggle-${t.id}`}>
                            {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                            <span className="min-w-0">
                              <span className="text-sm font-medium">{t.name}</span>
                              <span className="text-xs text-slate-400 ml-2">{objs.length} objective(s)</span>
                            </span>
                          </button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 shrink-0" onClick={() => del(t.id)} title="Delete template" data-testid={`template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        {isOpen && (
                          <div className="px-4 pb-3 space-y-2 border-t border-slate-100 pt-2">
                            {t.goal_statement && <p className="text-sm text-slate-700"><span className="font-semibold text-slate-500">Statement:</span> {t.goal_statement}</p>}
                            {objs.length > 0 ? (
                              <ol className="list-decimal ml-5 space-y-1">
                                {objs.map((o, i) => (
                                  <li key={i} className="text-sm text-slate-700">
                                    {o.description || <span className="italic text-slate-400">—</span>}
                                    {(o.measurement || o.progress_status) && (
                                      <span className="text-xs text-slate-400"> {o.progress_status ? `· ${o.progress_status}` : ""}{o.measurement ? ` · ${o.measurement}` : ""}</span>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            ) : (
                              <p className="text-xs italic text-slate-400">No objectives in this template.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
